import React, { useMemo } from 'react';
import { Platform, RefreshControl, Text, View, useWindowDimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';

import { DashboardScrollView } from '@/components/dashboard-scroll-view';
import { ProviderCarousel } from '@/components/discovery/provider-carousel';
import { AppointmentCard } from '@/components/schedule/appointment-card';
import { PendingRecurrenceCard } from '@/components/schedule/pending-recurrence-card';
import { RecurrenceAppointmentGroup } from '@/components/schedule/recurrence-appointment-group';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { Card, PressableCard } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { SkeletonCard } from '@/components/ui/skeleton';
import {
  SubscriptionQuotaCard,
  type SubscriptionQuotaSummary,
} from '@/components/schedule/subscription-quota-card';
import { TimelineFeed, type TimelineEvent } from '@/components/schedule/timeline-feed';
import { useClientAppointments } from '@/hooks/use-appointments';
import { useClientCharges } from '@/hooks/use-client-charges';
import { useClientEnrollments } from '@/hooks/use-enrollments';
import { useMyRecurrences } from '@/hooks/use-recurrences';
import { Appointment, APPOINTMENT_STATUS } from '@/types/appointment';
import { Charge } from '@/types/charge';
import { ENROLLMENT_STATUS } from '@/types/enrollment-status';
import { QUOTA_PERIOD } from '@/types/quota-period';
import { cn } from '@/utils/cn';
import { isChargeOpen, isChargeOverdue } from '@/utils/charge-status';
import { formatAppointmentWhen } from '@/utils/format-appointment';
import { formatCurrency } from '@/utils/format-currency';
import { formatDate, parseDateOnly } from '@/utils/format-date';
import { groupAppointmentsByRecurrence, type AppointmentGroup } from '@/utils/group-appointments';

const MAX_UPCOMING_GROUPS = 4;

/**
 * Largura a partir da qual a home deixa de ser uma coluna: no monitor, empilhar
 * linha do tempo, descoberta e assinatura numa faixa de 672px desperdiça metade
 * da tela. Acima disso a coluna da direita carrega o que é contexto, não ação.
 */
const TWO_COLUMN_BREAKPOINT = 1024;

/** Janela de cota vigente (a cota não acumula entre períodos). */
function quotaWindow(period: QUOTA_PERIOD, now: Date) {
  if (period === QUOTA_PERIOD.WEEK) {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    // Semana começa na segunda: domingo (0) recua 6 dias, não 0.
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
    const end = new Date(start);
    end.setDate(end.getDate() + 7);
    return { start, end };
  }
  return {
    start: new Date(now.getFullYear(), now.getMonth(), 1),
    end: new Date(now.getFullYear(), now.getMonth() + 1, 1),
  };
}

function groupHead(group: AppointmentGroup): Appointment {
  return group.type === 'single' ? group.appointment : group.appointments[0];
}

function groupKey(group: AppointmentGroup): string {
  return group.type === 'single' ? group.appointment.id : group.enrollmentId;
}

function OverdueChargesCard({ count, total }: { count: number; total: number }) {
  return (
    <Card className="gap-3 border-red-100 bg-red-50 p-4">
      <View className="flex-row items-start justify-between gap-3">
        <View className="flex-1 gap-0.5">
          <ThemedText className="font-semibold text-base text-red-700">
            {count > 1 ? `${count} cobranças atrasadas` : 'Cobrança atrasada'}
          </ThemedText>
          <ThemedText className="text-sm text-red-600">
            Regularize para continuar agendando sem contratempo
          </ThemedText>
        </View>
        <ThemedText
          className="font-bold text-lg text-red-700"
          style={{ fontVariant: ['tabular-nums'] }}
        >
          {formatCurrency(total)}
        </ThemedText>
      </View>
      <Button
        title="Ver cobrança"
        size="sm"
        onPress={() => router.push('/(tabs)/(client)/payments')}
      />
    </Card>
  );
}

function UpcomingChargeCard({ charge, serviceName }: { charge: Charge; serviceName?: string }) {
  return (
    <PressableCard
      className="flex-row items-center justify-between gap-3 p-4"
      onPress={() => router.push('/(tabs)/(client)/payments')}
    >
      <View className="flex-1 gap-0.5">
        <ThemedText className="font-semibold text-sm" numberOfLines={1}>
          {serviceName ?? 'Cobrança'}
        </ThemedText>
        <ThemedText className="text-xs text-neutral-500">
          Vence em {formatDate(charge.dueDate)}
        </ThemedText>
      </View>
      <ThemedText className="font-bold text-base" style={{ fontVariant: ['tabular-nums'] }}>
        {formatCurrency(charge.amount)}
      </ThemedText>
    </PressableCard>
  );
}

export default function ClientDashboardScreen() {
  const { width } = useWindowDimensions();
  // Só na web: no app nativo nem tablet chega perto do ponto de virada.
  const twoColumn = Platform.OS === 'web' && width >= TWO_COLUMN_BREAKPOINT;

  const {
    data: appointments,
    isLoading: appointmentsLoading,
    error: appointmentsError,
    refetch: refetchAppointments,
    isRefetching,
  } = useClientAppointments();
  const { data: recurrences, refetch: refetchRecurrences } = useMyRecurrences();
  const { data: enrollments, isLoading: enrollmentsLoading } = useClientEnrollments();
  // Cobranças vêm da rota própria: avulso não tem contrato, não está em enrollment.charges.
  const {
    data: clientCharges,
    isLoading: chargesLoading,
    refetch: refetchCharges,
  } = useClientCharges();

  const pendingRecurrences = useMemo(
    () => (recurrences ?? []).filter((r) => r.status === ENROLLMENT_STATUS.PENDING_APPROVAL),
    [recurrences],
  );

  const upcomingGroups = useMemo(() => {
    const list = appointments ?? [];
    const now = Date.now();
    const upcoming = list
      .filter(
        (a) => new Date(a.startAt).getTime() > now && a.status !== APPOINTMENT_STATUS.CANCELLED,
      )
      .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
    return groupAppointmentsByRecurrence(upcoming);
  }, [appointments]);

  const { overdueCount, overdueTotal, nextCharge, nextChargeServiceName } = useMemo(() => {
    const openCharges = (clientCharges ?? [])
      .map((charge) => ({ charge, serviceName: charge.serviceName ?? undefined }))
      .filter((item) => isChargeOpen(item.charge))
      .sort(
        (a, b) =>
          (parseDateOnly(a.charge.dueDate)?.getTime() ?? 0) -
          (parseDateOnly(b.charge.dueDate)?.getTime() ?? 0),
      );

    const overdue = openCharges.filter((item) => isChargeOverdue(item.charge));
    const next = openCharges.find((item) => !isChargeOverdue(item.charge)) ?? null;

    return {
      overdueCount: overdue.length,
      overdueTotal: overdue.reduce((sum, item) => sum + Number(item.charge.amount), 0),
      nextCharge: next?.charge ?? null,
      nextChargeServiceName: next?.serviceName,
    };
  }, [clientCharges]);

  const subscriptionSummaries = useMemo<SubscriptionQuotaSummary[]>(() => {
    const now = new Date();
    return (enrollments ?? [])
      .filter((e) => e.status === ENROLLMENT_STATUS.ACTIVE && (e.quotaPerPeriod ?? 0) > 0)
      .map((e) => {
        const period = e.quotaPeriod ?? QUOTA_PERIOD.MONTH;
        const { start, end } = quotaWindow(period, now);
        const used = (appointments ?? []).filter((a) => {
          if (a.enrollmentId !== e.id) return false;
          if (a.status === APPOINTMENT_STATUS.CANCELLED) return false;
          const at = new Date(a.startAt).getTime();
          return at >= start.getTime() && at < end.getTime();
        }).length;

        return {
          enrollmentId: e.id,
          serviceName: e.service?.name ?? 'Assinatura',
          quota: e.quotaPerPeriod as number,
          used,
          renewsAt: end,
          periodWord: period === QUOTA_PERIOD.WEEK ? ('semana' as const) : ('mês' as const),
        };
      });
  }, [enrollments, appointments]);

  const openAppointment = (appointment: Appointment) =>
    router.push({
      pathname: '/(tabs)/(client)/appointments/[id]',
      params: { id: appointment.id },
    });

  const visibleGroups = upcomingGroups.slice(0, MAX_UPCOMING_GROUPS);
  const hasMoreGroups = upcomingGroups.length > visibleGroups.length;

  const events: TimelineEvent[] = [];
  if (overdueCount > 0) {
    events.push({
      key: 'overdue',
      day: 'Atrasado',
      tone: 'urgent',
      content: <OverdueChargesCard count={overdueCount} total={overdueTotal} />,
    });
  }
  visibleGroups.forEach((group, index) => {
    const head = groupHead(group);
    events.push({
      key: groupKey(group),
      day: formatAppointmentWhen(head.startAt),
      tone: index === 0 && overdueCount === 0 ? 'hot' : 'default',
      content:
        group.type === 'single' ? (
          <AppointmentCard appointment={group.appointment} onOpen={openAppointment} />
        ) : (
          <RecurrenceAppointmentGroup
            enrollmentId={group.enrollmentId}
            appointments={group.appointments}
            onOpen={openAppointment}
            canBook
          />
        ),
    });
  });
  pendingRecurrences.forEach((item) => {
    events.push({
      key: `pending-${item.id}`,
      day: 'Aguardando aprovação',
      tone: 'warn',
      content: <PendingRecurrenceCard item={item} />,
    });
  });
  if (nextCharge) {
    events.push({
      key: `next-charge-${nextCharge.id}`,
      day: 'Cobrança a vencer',
      tone: 'default',
      content: <UpcomingChargeCard charge={nextCharge} serviceName={nextChargeServiceName} />,
    });
  }

  const isLoading = appointmentsLoading || enrollmentsLoading || chargesLoading;
  const isEmpty = events.length === 0;

  const timelineColumn = isLoading ? (
    <View className="gap-3">
      {Array.from({ length: 2 }).map((_, i) => (
        <SkeletonCard key={i} showHeader lines={2} />
      ))}
    </View>
  ) : appointmentsError ? (
    <ErrorState
      variant="inline"
      title="Erro ao carregar sua agenda"
      message="Não foi possível carregar seus agendamentos."
      onRetry={refetchAppointments}
    />
  ) : isEmpty ? (
    <EmptyState
      icon="calendar-outline"
      title="Vamos marcar o primeiro?"
      description="Escolha um prestador perto de você e marque em menos de um minuto."
      actionLabel="Buscar prestador"
      onAction={() => router.push('/buscar')}
    />
  ) : (
    <>
      <TimelineFeed events={events} />
      {hasMoreGroups && (
        <Button
          variant="outline"
          size="sm"
          className="self-start"
          title="Ver todos os agendamentos"
          onPress={() => router.push('/(tabs)/(client)/appointments')}
        />
      )}
    </>
  );

  const subscriptionCards = subscriptionSummaries.map((summary) => (
    <SubscriptionQuotaCard key={summary.enrollmentId} summary={summary} />
  ));

  const wrapClass = twoColumn ? 'max-w-[1040px]' : 'max-w-2xl';

  return (
    <>
      <View className="flex-1 flex-grow items-center pt-5">
        <DashboardScrollView
          className="w-full bg-canvas"
          contentContainerStyle={{ paddingBottom: 80 }}
          refreshControl={
            <RefreshControl
              refreshing={isRefetching}
              onRefresh={() => {
                void refetchAppointments();
                void refetchRecurrences();
                void refetchCharges();
              }}
            />
          }
        >
          <View className="w-full items-center justify-center gap-6 p-5">
            <View className={cn('w-full flex-row items-start justify-between gap-3', wrapClass)}>
              <View className="flex-1 gap-0.5">
                <ThemedText className="font-bold text-lg text-foreground">
                  Sua linha do tempo
                </ThemedText>
                <ThemedText className="text-sm text-neutral-500">
                  Horários e cobranças, na ordem em que acontecem
                </ThemedText>
              </View>
              <Button size="sm" onPress={() => router.push('/(tabs)/(client)/booking')}>
                <View className="flex-row items-center gap-1.5">
                  <Ionicons name="add" size={16} color="#fff" />
                  <Text className="font-semibold text-sm text-white">Agendar</Text>
                </View>
              </Button>
            </View>

            {twoColumn ? (
              <View className={cn('w-full flex-row items-start gap-6', wrapClass)}>
                <View className="min-w-0" style={{ flex: 1.35 }}>
                  {timelineColumn}
                </View>
                <View className="min-w-0 gap-5" style={{ flex: 1 }}>
                  {subscriptionCards}
                  <ProviderCarousel className="max-w-none" />
                </View>
              </View>
            ) : (
              <>
                <View className={cn('w-full', wrapClass)}>{timelineColumn}</View>
                {subscriptionCards.length > 0 && (
                  <View className={cn('w-full gap-3', wrapClass)}>{subscriptionCards}</View>
                )}
                <ProviderCarousel />
              </>
            )}
          </View>
        </DashboardScrollView>
      </View>
    </>
  );
}
